import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Mic, Square, RotateCcw, Loader2, Upload } from "lucide-react";
import useMicRecorder from "../../hooks/useMicRecorder";
import { CLONE_SCRIPT, CLONE_SCRIPT_EXTRA, MIN_SECONDS, MAX_SECONDS } from "./cloneScript";
import { TOK, SERIF, SANS } from "../../pages/Soulumo/SignatureBits";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const ERRORS = {
  sample_too_short: "That take was a little short. Please keep reading until the timer says you can finish.",
  audio_unreadable: "We couldn't read that recording. Please try again.",
  sample_too_large: "That recording is too large. Please keep it under 3 minutes.",
  voice_clone_not_enabled: "My voice isn't switched on for your account yet.",
};

const PillBtn = ({ children, primary, ...p }) => (
  <button type="button" {...p}
    className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[0.68rem] uppercase tracking-[0.22em] transition-[opacity,transform] duration-200 active:scale-[0.98] disabled:opacity-40"
    style={{ fontFamily: SANS, minHeight: 48, background: primary ? TOK.gold : "transparent", color: primary ? TOK.ink : TOK.gold, border: `1px solid ${TOK.gold}` }}>
    {children}
  </button>
);

export const CloneRecorder = ({ onUploaded, minSeconds = MIN_SECONDS }) => {
  const { state, blob, mime, error, elapsed, start, stop, reset } = useMicRecorder();
  const [progress, setProgress] = useState(null);
  const [uploadErr, setUploadErr] = useState("");
  const [consent, setConsent] = useState(false);
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);

  useEffect(() => { if (state === "recording" && elapsed >= MAX_SECONDS) stop(); }, [state, elapsed, stop]);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);

  const upload = async () => {
    setUploadErr("");
    setProgress(0);
    const fd = new FormData();
    const ext = mime.includes("mp4") ? "m4a" : mime.includes("ogg") ? "ogg" : mime.includes("mpeg") ? "mp3" : "webm";
    fd.append("file", blob, `sample.${ext}`);
    try {
      const r = await axios.post(`${API}/api/voice-clone/sample`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` },
        onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
      });
      onUploaded(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail || "";
      setUploadErr(ERRORS[d.split(":")[0]] || "Upload failed. Please check your connection and try again.");
      setProgress(null);
    }
  };

  const tooShort = elapsed < minSeconds;
  const lines = minSeconds > MIN_SECONDS ? [...CLONE_SCRIPT, ...CLONE_SCRIPT_EXTRA] : CLONE_SCRIPT;
  return (
    <div data-testid="clone-recorder">
      <div className="rounded-3xl px-5 py-6 mb-8 space-y-4" style={{ background: "rgba(245,236,217,0.03)", border: `1px solid ${TOK.goldMist}` }} data-testid="clone-script">
        <div className="text-[0.58rem] uppercase tracking-[0.3em]" style={{ color: TOK.goldSoft, fontFamily: SANS }}>Read aloud, slowly{minSeconds > MIN_SECONDS ? " · about 2 minutes" : ""}</div>
        {lines.map((line) => (
          <p key={line} className="text-[1.15rem] leading-relaxed" style={{ fontFamily: SERIF, color: TOK.cream }}>{line}</p>
        ))}
      </div>

      <div className="flex flex-col items-center gap-4">
        <div className="text-3xl font-light tabular-nums" style={{ fontFamily: SERIF, color: state === "recording" ? TOK.gold : TOK.creamDim }} data-testid="clone-timer">
          {fmt(elapsed)} <span className="text-sm" style={{ color: TOK.creamMist }}>/ {fmt(MAX_SECONDS)}</span>
        </div>
        {state === "recording" && tooShort && (
          <p className="text-xs" style={{ color: TOK.creamMist }} data-testid="clone-min-hint">Keep going, at least {fmt(minSeconds)} for a natural clone</p>
        )}
        {state === "idle" && (
          <div className="space-y-3">
            <label className="flex items-start gap-3 text-xs py-2 cursor-pointer" style={{ color: TOK.creamDim, fontFamily: SANS }} data-testid="clone-consent">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="clone-consent-check" className="mt-0.5 w-5 h-5 shrink-0" />
              <span>This is my own voice, and I agree to Soulumo creating a voice model of it for my installations (<a href="/soulumo/privacy" target="_blank" rel="noreferrer" className="underline">privacy</a>).</span>
            </label>
            <PillBtn primary onClick={start} disabled={!consent} data-testid="clone-record-btn"><Mic size={14} /> Start recording</PillBtn>
          </div>
        )}
        {state === "recording" && (
          <PillBtn primary onClick={stop} disabled={tooShort} data-testid="clone-stop-btn"><Square size={12} /> {tooShort ? "Recording…" : "Finish"}</PillBtn>
        )}
        {state === "ready" && url && (
          <>
            <audio src={url} controls className="w-full max-w-sm" data-testid="clone-review-audio" />
            <div className="flex gap-3 flex-wrap justify-center">
              <PillBtn onClick={reset} disabled={progress !== null} data-testid="clone-rerecord-btn"><RotateCcw size={13} /> Record again</PillBtn>
              <PillBtn primary onClick={upload} disabled={progress !== null} data-testid="clone-create-btn">
                {progress !== null ? <><Loader2 size={13} className="animate-spin" /> {progress}%</> : <><Upload size={13} /> Create my voice</>}
              </PillBtn>
            </div>
          </>
        )}
        {(error || uploadErr) && (
          <p className="text-sm text-center" style={{ color: "#dc8b6c" }} data-testid="clone-error">
            {uploadErr || "We couldn't reach your microphone. Please allow mic access and try again."}
          </p>
        )}
      </div>
    </div>
  );
};
